export type EntitlementState =
  | 'loading'
  | 'trial_active'
  | 'subscription_active'
  | 'billing_grace'
  | 'access_expired'
  | 'billing_issue'
  | 'error';

export type EntitlementView = 'loading' | 'product' | 'recovery' | 'blocked';

export type TrialBadgeState =
  | { kind: 'hidden' }
  | { kind: 'days_left'; count: number }
  | { kind: 'ends_today' }
  | { kind: 'expired' };

export interface EntitlementSnapshot {
  allowed: boolean;
  reason?:
    | 'trial_expired'
    | 'payment_grace_expired'
    | 'subscription_inactive'
    | 'seat_limit_exceeded'
    | 'salon_not_found'
    | 'salon_membership_required'
    | 'account_access_denied'
    | 'salon_context_required'
    | 'licensing_lookup_failed';
  billing_status?: string;
  trial_ends_at?: string;
  payment_grace_until?: string;
}

function isCurrentOrFuture(value: string | undefined, now: Date): boolean {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) && timestamp >= now.getTime();
}

export function normalizeEntitlement(
  status: EntitlementSnapshot | null,
  hasError = false,
  now = new Date()
): EntitlementState {
  if (hasError) return 'error';
  if (!status) return 'loading';

  const billingStatus = String(status.billing_status || '').trim().toLowerCase();

  if (!status.allowed) {
    if (status.reason === 'trial_expired') return 'access_expired';
    if (
      status.reason === 'payment_grace_expired'
      || status.reason === 'subscription_inactive'
      || status.reason === 'seat_limit_exceeded'
    ) {
      return 'billing_issue';
    }
    return 'error';
  }

  if (billingStatus === 'active') return 'subscription_active';

  if (billingStatus === 'trialing') {
    return isCurrentOrFuture(status.trial_ends_at, now) ? 'trial_active' : 'access_expired';
  }

  if (billingStatus === 'past_due') {
    return isCurrentOrFuture(status.payment_grace_until, now) ? 'billing_grace' : 'billing_issue';
  }

  return 'error';
}

export function hasProductAccess(state: EntitlementState): boolean {
  return state === 'trial_active' || state === 'subscription_active' || state === 'billing_grace';
}

export function resolveEntitlementView(
  state: EntitlementState,
  isRecoveryRoute: boolean
): EntitlementView {
  if (isRecoveryRoute) return 'recovery';
  if (state === 'loading') return 'loading';
  return hasProductAccess(state) ? 'product' : 'blocked';
}

export function getTrialBadgeState(
  status: EntitlementSnapshot | null,
  now = new Date()
): TrialBadgeState {
  if (String(status?.billing_status || '').trim().toLowerCase() !== 'trialing') {
    return { kind: 'hidden' };
  }

  if (!status?.trial_ends_at) return { kind: 'hidden' };

  const trialEnd = new Date(status.trial_ends_at).getTime();
  if (!Number.isFinite(trialEnd)) return { kind: 'hidden' };

  const daysRemaining = Math.ceil((trialEnd - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysRemaining < 0) return { kind: 'expired' };
  if (daysRemaining <= 1) return { kind: 'ends_today' };
  return { kind: 'days_left', count: daysRemaining };
}